export default function ProbateProcess() {
  const stages = [
    {
      stage: 'Filing & Appointment',
      duration: '1-3 months',
      description: 'Petition filed with probate court, executor/administrator appointed, initial hearings scheduled.',
      challenges: 'Court backlogs, document preparation, notification requirements'
    },
    {
      stage: 'Asset Inventory',
      duration: '2-4 months',
      description: 'Complete inventory of estate assets, property appraisals, creditor notifications published.',
      challenges: 'Finding all assets, obtaining valuations, identifying creditors'
    },
    {
      stage: 'Creditor Claims Period',
      duration: '3-6 months',
      description: 'Legal waiting period for creditors to file claims against the estate.',
      challenges: 'Cannot sell property until this period expires in most states'
    },
    {
      stage: 'Court Approval to Sell',
      duration: '1-2 months',
      description: 'Petition court for permission to sell real estate, obtain approval order.',
      challenges: 'Court schedules, objections from heirs, appraisal requirements'
    },
    {
      stage: 'Sale & Distribution',
      duration: '2-6 months',
      description: 'Market property, accept offer, close transaction, distribute proceeds.',
      challenges: 'Finding qualified buyers, financing delays, title issues'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Navigating the Probate Process
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding probate timelines helps you plan effectively. Here's what to expect when selling an inherited apartment building through probate court.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-10 mb-12">
          <div className="space-y-8">
            {stages.map((item, index) => (
              <div key={index} className="relative pl-12 pb-8 border-l-4 border-teal-500 last:border-l-0 last:pb-0">
                <div className="absolute -left-4 top-0 w-8 h-8 flex items-center justify-center bg-teal-500 text-white rounded-full font-bold">
                  {index + 1}
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex flex-wrap items-center justify-between mb-3">
                    <h3 className="text-2xl font-bold text-gray-900">{item.stage}</h3>
                    <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                      {item.duration}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">{item.description}</p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      <strong className="text-gray-900">Common Challenges:</strong> {item.challenges}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-red-500 text-white rounded-lg mb-6">
              <i className="ri-time-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Traditional Timeline</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The complete probate process typically takes <strong className="text-gray-900">9-18 months</strong> from start to finish. During this time, you're responsible for:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-red-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Monthly mortgage payments and property taxes</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-red-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Insurance, utilities, and maintenance costs</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-red-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Tenant management and emergency repairs</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-red-500 text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Legal fees and court costs</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl p-8 shadow-lg text-white">
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg mb-6">
              <i className="ri-flashlight-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Our Expedited Process</h3>
            <p className="text-white/90 mb-4 leading-relaxed">
              We work directly with probate attorneys and courts to accelerate the sale process. Our experience with inherited properties means:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                <span className="text-white/90">Cash offers eliminate financing contingencies</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                <span className="text-white/90">We buy AS IS—no repairs or updates needed</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                <span className="text-white/90">Flexible closing dates to match court approval</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                <span className="text-white/90">Close in as little as 14-30 days after approval</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
